import React, {useEffect} from 'react'
import PropTypes from 'prop-types'
import {Box, Grid, Text, useBreakpointValue, useStyleConfig} from '@chakra-ui/react'
import {useProducts} from '@salesforce/commerce-sdk-react'
import {debounce} from 'lodash'
import {PricingTile} from './pricing-tile'
import {remCalc} from '../../../theme/_utils'

export const Pricing = ({heading = '', bodycopy = '', pricingGroup = []}) => {
    const styles = useStyleConfig('Pricing')

    const products = pricingGroup
        .map((group) =>
            group.products?.data?.length && group.products?.data?.length > 0
                ? {
                      productId: group.products.data[0].id,
                      isBestSeller: group.is_best_seller,
                      timing: group.timing,
                  }
                : null,
        )
        .filter((product) => !!product)

    const {data: productsData} = useProducts({
        parameters: {
            ids: products.map((product) => product.productId),
            allImages: false,
        },
    })

    const productsNumber = productsData?.data?.length || 0

    const tilesGridClass = useBreakpointValue({
        base: '',
        md: `tablet-tiles-grid-${productsNumber}`,
        lg: `desktop-tiles-grid-${productsNumber}`,
    })

    const repeat = useBreakpointValue({
        base: 1,
        md: Math.min(...[2, productsNumber]),
        lg: Math.min(...[4, productsNumber]),
    })

    const isMobile = useBreakpointValue({base: true, md: false})
    const containerHasPaddingTop = !!heading || !!bodycopy

    const CONTAINERS_SELECTOR = '.pricing-tile'
    const RESIZABLES_SELECTOR = '.pricing-resizable'
    const DEBOUNCE_WAIT = 10
    const debouncedResize = debounce(() => {
        const containers = [...document.querySelectorAll(CONTAINERS_SELECTOR)]

        if (isMobile) {
            containers.forEach((container) => {
                Array.from(container.querySelectorAll(RESIZABLES_SELECTOR)).forEach((resizable) => {
                    resizable.style.minHeight = null
                })
            })
        } else {
            const containersResizablesHeights = containers.map((container) =>
                [...container.querySelectorAll(RESIZABLES_SELECTOR)].map((resizable) => {
                    resizable.style.minHeight = null
                    return resizable.offsetHeight
                }),
            )
            const maxHeights = new Array(
                Math.max(...containersResizablesHeights.map((c) => c.length)),
            )
                .fill(null)
                .map((_, i) => Math.max(...containersResizablesHeights.map((h) => h[i])))
            containers.forEach((container) => {
                Array.from(container.querySelectorAll(RESIZABLES_SELECTOR)).forEach(
                    (resizable, i) => {
                        resizable.style.minHeight = `${maxHeights[i]}px`
                    },
                )
            })
        }
    }, DEBOUNCE_WAIT)

    useEffect(() => {
        debouncedResize()
        window.addEventListener('resize', debouncedResize)

        return () => window.removeEventListener('resize', debouncedResize)
    }, [productsData, isMobile])

    return (
        <Box
            data-testid="pricing"
            __css={{...styles.container}}
            pt={containerHasPaddingTop ? {base: remCalc(16), md: remCalc(24)} : 0}
        >
            {heading && (
                <Text as="h2" {...styles.heading}>
                    {heading}
                </Text>
            )}
            {bodycopy && <Box {...styles.bodycopy} dangerouslySetInnerHTML={{__html: bodycopy}} />}
            {productsData?.data?.length && productsData?.data?.length > 0 && (
                <Grid
                    templateColumns={`repeat(${repeat}, 1fr)`}
                    data-testid="pricing-tiles"
                    {...styles.tiles}
                    className={tilesGridClass}
                >
                    {productsData?.data.map((productData, tIndex) => (
                        <PricingTile
                            key={`pricing-tile-${tIndex}`}
                            productData={productData}
                            {...products.find((product) => product.productId === productData.id)}
                        />
                    ))}
                </Grid>
            )}
        </Box>
    )
}

Pricing.propTypes = {
    heading: PropTypes.string,
    bodycopy: PropTypes.string,
    pricingGroup: PropTypes.arrayOf(PropTypes.shape()),
}
